import React, { useEffect, useState, lazy, Suspense } from 'react';
import { Leaf, MapPin } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { statsAPI, ordersAPI, adminAPI, settingsAPI } from '../lib/api';
import { ORDER_STATUS } from '../lib/utils';
import { toast } from 'sonner';
import api from '../lib/api';

import { DashboardStatsCards } from '../components/admin/DashboardStatsCards';
import { VisitorStatsCard } from '../components/admin/VisitorStatsCard';
import { HomepageSettingsCard } from '../components/admin/HomepageSettingsCard';
import { SalesCharts } from '../components/admin/SalesCharts';
import { TopClientsCard } from '../components/admin/TopClientsCard';
import { RecentOrdersCard } from '../components/admin/RecentOrdersCard';
import { EmailStatsCard } from '../components/admin/EmailStatsCard';

const MapView = lazy(() => import('../components/MapView'));

export const AdminDashboard = () => {
    const [stats, setStats] = useState(null);
    const [emailStats, setEmailStats] = useState(null);
    const [visitorStats, setVisitorStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [homepageSettings, setHomepageSettings] = useState({});
    const [promoDateInput, setPromoDateInput] = useState('');
    const [mapClients, setMapClients] = useState([]);

    useEffect(() => {
        const loadStats = async () => {
            try {
                const [res, emailRes, settingsRes, visitorRes, mapRes] = await Promise.all([
                    statsAPI.getAdminStats(),
                    adminAPI.getEmailStats(),
                    settingsAPI.getAll(),
                    settingsAPI.getVisitorStats().catch(() => ({ data: { today: 0, total_unique: 0, total_visits: 0, daily: [] } })),
                    api.get('/admin/clients/map').catch(() => ({ data: { clients: [] } }))
                ]);
                setStats(res.data);
                setEmailStats(emailRes.data);
                setHomepageSettings(settingsRes.data || {});
                setVisitorStats(visitorRes.data);
                setMapClients(mapRes.data.clients.map(c => ({ ...c, label: c.company_name })));
            } catch (error) {
                console.error('Error loading stats:', error);
            } finally {
                setLoading(false);
            }
        };
        loadStats();
    }, []);

    const handleToggleSetting = async (key) => {
        const newValue = !homepageSettings[key];
        setHomepageSettings(prev => ({ ...prev, [key]: newValue }));
        try {
            await settingsAPI.update(key, newValue);
            toast.success(newValue ? 'Section activee sur la page d\'accueil' : 'Section masquee de la page d\'accueil');
        } catch (error) {
            setHomepageSettings(prev => ({ ...prev, [key]: !newValue }));
            toast.error('Erreur lors de la mise a jour');
        }
    };

    const handleSetPromoDate = async () => {
        if (!promoDateInput) return;
        try {
            await settingsAPI.update('promo_end_date', promoDateInput);
            setHomepageSettings(prev => ({ ...prev, promo_end_date: promoDateInput }));
            toast.success('Compte a rebours promo active');
        } catch {
            toast.error('Erreur lors de la mise a jour');
        }
    };

    const handleClearPromoDate = async () => {
        try {
            await settingsAPI.update('promo_end_date', null);
            setHomepageSettings(prev => ({ ...prev, promo_end_date: null }));
            setPromoDateInput('');
            toast.success('Compte a rebours promo desactive');
        } catch {
            toast.error('Erreur lors de la suppression');
        }
    };

    const handleStatusChange = async (orderId, newStatus) => {
        try {
            await ordersAPI.updateStatus(orderId, newStatus);
            toast.success('Statut mis a jour');
            const res = await statsAPI.getAdminStats();
            setStats(res.data);
        } catch (error) {
            toast.error('Erreur lors de la mise a jour');
        }
    };

    const chartData = stats?.monthly_stats?.map(m => ({
        month: m._id,
        commandes: m.orders,
        revenue: m.revenue,
    })).reverse() || [];

    const statusData = stats?.orders_by_status?.map(s => ({
        name: ORDER_STATUS[s._id]?.label || s._id,
        value: s.count,
    })) || [];

    if (loading) {
        return (
            <div className="min-h-screen bg-sand flex items-center justify-center">
                <div className="animate-pulse text-muted-foreground">Chargement...</div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-sand bg-botanical" data-testid="admin-dashboard">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-xl bg-jungle flex items-center justify-center">
                            <Leaf className="w-6 h-6 text-sand" />
                        </div>
                        <div>
                            <h1 className="font-heading text-3xl font-bold tracking-tight text-jungle">
                                Administration PRIXDEAL
                            </h1>
                            <p className="text-muted-foreground">Vue d'ensemble de votre activite</p>
                        </div>
                    </div>
                </div>

                <DashboardStatsCards stats={stats} />
                <VisitorStatsCard visitorStats={visitorStats} />
                <HomepageSettingsCard
                    homepageSettings={homepageSettings}
                    promoDateInput={promoDateInput}
                    setPromoDateInput={setPromoDateInput}
                    onToggleSetting={handleToggleSetting}
                    onSetPromoDate={handleSetPromoDate}
                    onClearPromoDate={handleClearPromoDate}
                />
                <SalesCharts chartData={chartData} statusData={statusData} />
                <TopClientsCard topClients={stats?.top_clients} />

                {/* Carte des clients */}
                {mapClients.length > 0 && (
                    <Card className="mb-8 border-0 bg-white/80 backdrop-blur-sm shadow-sm rounded-2xl" data-testid="dashboard-map">
                        <CardHeader>
                            <CardTitle className="font-heading text-jungle flex items-center gap-2">
                                <MapPin className="h-5 w-5" />
                                Carte des clients ({mapClients.length})
                            </CardTitle>
                        </CardHeader>
                        <CardContent>
                            <Suspense fallback={<div className="h-80 bg-muted animate-pulse rounded-xl" />}>
                                <MapView markers={mapClients} height="350px" />
                            </Suspense>
                        </CardContent>
                    </Card>
                )}

                <RecentOrdersCard recentOrders={stats?.recent_orders} onStatusChange={handleStatusChange} />
                <EmailStatsCard emailStats={emailStats} />
            </div>
        </div>
    );
};
